import { useCallback, useState } from "react"
import { AxiosResponse } from "axios"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  datafileUpload,
  datafileGet,
  datafileDelete,
  datafileCombine,
  datafileGenerateDQD,
  generateDatamap,
} from "api/Repos/data-import"
import { DATAFILE_TYPE, DatafileSizeOption } from "types/dataImport.types"
import { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"
import { RcFile } from "antd/es/upload/interface"

/**
 * Data-import operations. These are commands in an orchestration flow
 * (upload → combine/generate → re-check), so they stay imperative: the
 * commands run through react-query mutations (for isPending + devtools),
 * the file-presence probe is a plain authed call. No query cache — the
 * containers own the resulting state and their own refresh sequencing.
 */
interface UseDataImportReturn {
  uploadDataImportFile: (
    id: string,
    owner: string,
    type: DATAFILE_TYPE,
    file: File | Blob | RcFile
  ) => Promise<AxiosResponse<unknown>>
  isUploadingDataimportFile: boolean

  getDataFilesUploaded: (
    id: string,
    owner: string,
    type: DATAFILE_TYPE,
    size?: DatafileSizeOption
  ) => Promise<AxiosResponse<unknown>>
  isGettingDataFilesUploaded: boolean

  deleteDataImportFile: (
    id: string,
    owner: string,
    type: DATAFILE_TYPE
  ) => Promise<AxiosResponse<unknown>>
  isDeletingDataimportFile: boolean

  combineDataFiles: (id: string) => Promise<AxiosResponse<unknown>>
  isCombiningDataFiles: boolean

  generateDQDFromJSON: (id: string) => Promise<AxiosResponse<unknown>>
  isGeneratingDQD: boolean

  generateMapFile: (
    id: string,
    groupSections?: boolean,
    variableNames?: EXPORT_VARIABLE_TYPES,
    variableStyle?: VARIABLE_STYLE,
    includeQuestionText?: boolean
  ) => Promise<AxiosResponse<unknown>>
  isGeneratingMapFile: boolean
}

export const useDataImport = (): UseDataImportReturn => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  // A completed import changes the data's metadata, the drift verdict and the project stamp,
  // and adds a data.imported record to the history: refresh every cached provenance read.
  const invalidateProvenance = (id: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.provenance.project(id) })

  const uploadMutation = useMutation({
    mutationFn: async (
      args: [string, string, DATAFILE_TYPE, File | Blob | RcFile]
    ) => datafileUpload(await getAxios(), ...args),
    // A direct DQD upload is the import (the server records it); csv/map/json uploads are
    // inputs to a later combine/generate step, which invalidates on its own success.
    onSuccess: (_res, args) => {
      if (args[2] === DATAFILE_TYPE.DQD) invalidateProvenance(args[0])
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (args: [string, string, DATAFILE_TYPE]) =>
      datafileDelete(await getAxios(), ...args),
  })

  const combineMutation = useMutation({
    mutationFn: async (id: string) => datafileCombine(await getAxios(), id),
    onSuccess: (_res, id) => invalidateProvenance(id),
  })

  const generateDQDMutation = useMutation({
    mutationFn: async (id: string) =>
      datafileGenerateDQD(await getAxios(), id),
    onSuccess: (_res, id) => invalidateProvenance(id),
  })

  const generateMapMutation = useMutation({
    mutationFn: async (
      args: [
        string,
        boolean | undefined,
        EXPORT_VARIABLE_TYPES | undefined,
        VARIABLE_STYLE | undefined,
        boolean | undefined
      ]
    ) => generateDatamap(await getAxios(), ...args),
  })

  const [isGettingDataFilesUploaded, setIsGettingDataFilesUploaded] =
    useState(false)

  const getDataFilesUploaded = useCallback(
    async (
      id: string,
      owner: string,
      type: DATAFILE_TYPE,
      size?: DatafileSizeOption
    ): Promise<AxiosResponse<unknown>> => {
      setIsGettingDataFilesUploaded(true)
      try {
        return await datafileGet(await getAxios(), id, owner, type, size)
      } finally {
        setIsGettingDataFilesUploaded(false)
      }
    },
    [getAxios]
  )

  const uploadDataImportFile = useCallback(
    (
      id: string,
      owner: string,
      type: DATAFILE_TYPE,
      file: File | Blob | RcFile
    ) => uploadMutation.mutateAsync([id, owner, type, file]),
    [uploadMutation.mutateAsync]
  )

  const deleteDataImportFile = useCallback(
    (id: string, owner: string, type: DATAFILE_TYPE) =>
      deleteMutation.mutateAsync([id, owner, type]),
    [deleteMutation.mutateAsync]
  )

  const generateMapFile = useCallback(
    (
      id: string,
      groupSections?: boolean,
      variableNames?: EXPORT_VARIABLE_TYPES,
      variableStyle?: VARIABLE_STYLE,
      includeQuestionText?: boolean
    ) =>
      generateMapMutation.mutateAsync([
        id,
        groupSections,
        variableNames,
        variableStyle,
        includeQuestionText,
      ]),
    [generateMapMutation.mutateAsync]
  )

  return {
    uploadDataImportFile,
    isUploadingDataimportFile: uploadMutation.isPending,

    getDataFilesUploaded,
    isGettingDataFilesUploaded,

    deleteDataImportFile,
    isDeletingDataimportFile: deleteMutation.isPending,

    combineDataFiles: combineMutation.mutateAsync,
    isCombiningDataFiles: combineMutation.isPending,

    generateDQDFromJSON: generateDQDMutation.mutateAsync,
    isGeneratingDQD: generateDQDMutation.isPending,

    generateMapFile,
    isGeneratingMapFile: generateMapMutation.isPending,
  }
}
